<!-- src/components/ToyRent/partials/ToyRentAddExtraTimeModalForm.vue -->
<template>
  <b-form novalidate @submit.prevent="handleAddExtra">
    <!-- ============ [A] TEMPO ============ -->
    <b-form-group
      label="Tempo em Minutos"
      label-for="time"
      :class="validationClass"
    >
      <b-input-group>
        <b-input-group-prepend>
          <b-button variant="outline-secondary" @click="decrementTime"
            >-</b-button
          >
        </b-input-group-prepend>

        <b-form-input
          id="time"
          v-model.number="form.time"
          :state="validation.time"
          type="number"
          min="1"
          placeholder="Informe o tempo"
        />
        <b-input-group-append>
          <b-button variant="outline-secondary" @click="incrementTime"
            >+</b-button
          >
        </b-input-group-append>
      </b-input-group>

      <b-form-invalid-feedback v-if="$v.form.time.$invalid">
        Informe um tempo válido (mínimo 1 minuto)
      </b-form-invalid-feedback>
    </b-form-group>

    <!-- ============ [B] MOTIVO ============ -->
    <b-form-group
      label="Motivo"
      label-for="reason"
      :class="reasonValidationClass"
    >
      <template v-if="!otherSelected">
        <b-form-select
          id="reason"
          v-model="form.reason"
          :state="validation.reason"
          @change="handleReasonChange"
        >
          <b-form-select-option :value="null" disabled>
            Selecione um motivo
          </b-form-select-option>
          <b-form-select-option
            v-for="(item, i) in reasons"
            :key="i"
            :value="item"
          >
            {{ item }}
          </b-form-select-option>
          <b-form-select-option value="Outro">Outro</b-form-select-option>
        </b-form-select>
      </template>

      <template v-else>
        <b-input-group>
          <b-form-input
            id="reason"
            v-model="form.reason"
            :state="validation.reason"
            type="text"
            placeholder="Informe o motivo"
          />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="resetOther"
              >X</b-button
            >
          </b-input-group-append>
        </b-input-group>
      </template>

      <b-form-invalid-feedback v-if="$v.form.reason.$invalid">
        Selecione ou informe um motivo
      </b-form-invalid-feedback>
    </b-form-group>

    <!-- ============ [C] BOTÃO ADICIONAR ============ -->
    <div class="mb-3">
      <b-button
        block
        variant="primary"
        type="submit"
        :disabled="creating || $v.form.$invalid"
      >
        <b-spinner v-if="creating" small />
        {{ creating ? 'Adicionando...' : 'Adicionar' }}
      </b-button>
    </div>

    <!-- ============ [D] LISTA EXISTENTES ============ -->
    <div class="extra-times-wrapper">
      <b-card no-body>
        <b-card-header
          class="py-2 d-flex justify-content-between align-items-center"
        >
          <span class="font-weight-bold">Tempos extras</span>
          <div class="small text-muted">
            {{ extras.length }} itens · Total:
            <strong>{{ totalMinutes }} min</strong>
          </div>
        </b-card-header>

        <div class="extras-list-container">
          <div v-if="loadingExtras" class="p-3">
            <div class="skeleton-line mb-2"></div>
            <div class="skeleton-line w-75 mb-2"></div>
            <div class="skeleton-line w-50"></div>
          </div>

          <div
            v-else-if="!extras.length"
            class="text-muted small p-4 text-center"
          >
            <b-icon-clock class="mb-2" font-scale="1.4" />
            <div>Nenhum tempo extra lançado para este aluguel.</div>
          </div>

          <b-list-group v-else flush>
            <b-list-group-item
              v-for="item in extras"
              :key="String(item.id)"
              class="extra-item d-flex justify-content-between align-items-start"
              :class="typeClass(item.type)"
            >
              <!-- Coluna do conteúdo -->
              <div class="content-col">
                <!-- Estado de Edição -->
                <div v-if="String(editingId) === String(item.id)">
                  <div class="edit-grid">
                    <b-form-input
                      v-model.number="editForm.minutes"
                      type="number"
                      size="sm"
                      placeholder="Min"
                      :disabled="saving"
                      aria-label="Minutos do tempo extra"
                      @keyup.enter="saveEdit(item)"
                    />
                    <b-form-input
                      v-model="editForm.reason"
                      type="text"
                      size="sm"
                      placeholder="Motivo (Obrigatorio)"
                      :disabled="saving"
                      @keyup.enter="saveEdit(item)"
                    />
                  </div>

                  <div class="d-flex">
                    <b-button
                      variant="link"
                      size="sm"
                      class="action-inline text-success"
                      title="Salvar alterações"
                      :disabled="saving || !canSaveEdit"
                      @click="saveEdit(item)"
                    >
                      <b-icon-check class="mr-1" />
                      <span class="action-label">Salvar</span>
                    </b-button>

                    <b-button
                      variant="link"
                      size="sm"
                      class="action-inline text-muted"
                      title="Cancelar edição"
                      :disabled="saving"
                      @click="cancelEdit"
                    >
                      <b-icon-x class="mr-1" />
                      <span class="action-label">Cancelar</span>
                    </b-button>
                  </div>
                </div>

                <!-- Estado Normal -->
                <div v-else>
                  <div class="d-flex align-items-center flex-wrap">
                    <b-badge
                      :variant="minutesVariant(item.minutes)"
                      class="mr-2"
                    >
                      {{ item.minutes }} min
                    </b-badge>

                    <span
                      v-b-tooltip.hover
                      class="text-muted reason-ellipsis"
                      :title="
                        item.reason && item.reason.trim().length
                          ? item.reason
                          : '—'
                      "
                    >
                      {{ truncated(item.reason, 40) }}
                    </span>
                  </div>

                  <div class="small text-black-50 mt-1">
                    <code class="id-chip">#{{ item.id }}</code>
                    <span class="mx-1">·</span>
                    <span>{{ formatDateTime(item.createdAt) }}</span>
                  </div>
                </div>
              </div>

              <!-- Coluna de Ações (vertical) -->
              <div class="actions-col">
                <template v-if="String(editingId) === String(item.id)">
                  <!-- Nada aqui quando editando (ações inline acima) -->
                </template>
                <template v-else>
                  <b-button
                    v-b-tooltip.hover
                    variant="link"
                    size="sm"
                    class="action-btn text-primary"
                    title="Editar"
                    aria-label="Editar tempo extra"
                    @click="beginEdit(item)"
                  >
                    <b-icon-pencil-square />
                  </b-button>

                  <b-button
                    v-b-tooltip.hover
                    variant="link"
                    size="sm"
                    class="action-btn text-danger"
                    title="Remover"
                    :disabled="removingId === String(item.id) || saving"
                    aria-label="Remover tempo extra"
                    @click="onClickRemove(item)"
                  >
                    <b-icon-trash />
                  </b-button>
                </template>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-card>
    </div>
  </b-form>
</template>

<script>
/**
 * Requer:
 *  - BootstrapVue + BootstrapVueIcons registrados no main.js
 *  - CSS do bootstrap-vue importado
 */
import { required, minValue, numeric } from 'vuelidate/lib/validators'
import withFormValidation from '@/mixins/withFormValidation'
import services from '@/services'

export default {
  name: 'ToyRentAddExtraTimeModalForm',
  mixins: [withFormValidation({ fields: ['time', 'reason'] })],
  validations: {
    form: {
      time: { required, numeric, minValue: minValue(1) },
      reason: { required }
    }
  },
  props: {
    toy: { type: Object, required: true },
    modalId: { type: String, default: null }
  },
  data() {
    return {
      form: { time: 0, reason: null },
      reasons: [
        'Autista',
        'Aniversario',
        'Troca de bateria',
        'Esquecido de Finalizar',
        'Erro do Sistema'
      ],
      otherSelected: false,
      creating: false,

      extras: [],
      loadingExtras: false,

      editingId: null,
      saving: false,
      editForm: { minutes: null, reason: '' },
      removingId: null
    }
  },
  computed: {
    validationClass() {
      return {
        'is-invalid': this.$v.form.time.$dirty && this.$v.form.time.$invalid
      }
    },
    reasonValidationClass() {
      return {
        'is-invalid': this.$v.form.reason.$dirty && this.$v.form.reason.$invalid
      }
    },
    rentId() {
      return this.toy?.rent?.id || this.toy?.rentId || null
    },
    canSaveEdit() {
      return (
        typeof this.editForm.minutes === 'number' &&
        this.editForm.minutes > 0 &&
        typeof this.editForm.reason === 'string' &&
        this.editForm.reason.trim().length > 0
      )
    },
    totalMinutes() {
      return this.extras.reduce((acc, x) => acc + (Number(x.minutes) || 0), 0)
    }
  },
  watch: {
    rentId: {
      immediate: true,
      handler(newId) {
        if (newId) this.loadExistingExtras()
        else this.extras = []
      }
    }
  },
  destroyed() {
    this.form.time = 0
    this.form.reason = ''
    this.editingId = null
    this.editForm.minutes = null
    this.editForm.reason = ''
  },
  methods: {
    truncated(text, max = 40) {
      if (!text) return '—'
      const t = String(text).trim()
      return t.length > max ? `${t.slice(0, max)}…` : t
    },
    minutesVariant(min) {
      if (min >= 60) return 'danger'
      if (min >= 30) return 'warning'
      return 'primary'
    },
    typeClass(t) {
      switch ((t || '').toLowerCase()) {
        case 'pause':
          return 'type-pause'
        case 'bonus':
          return 'type-bonus'
        default:
          return 'type-default'
      }
    },

    incrementTime() {
      this.form.time = (Number(this.form.time) || 0) + 1
    },
    decrementTime() {
      this.form.time = Number(this.form.time) || 0
      if (this.form.time > 0) this.form.time--
    },
    handleReasonChange(value) {
      this.otherSelected = value === 'Outro'
      if (this.otherSelected) this.form.reason = ''
    },
    resetOther() {
      this.otherSelected = false
      this.form.reason = null
    },

    async handleAddExtra() {
      this.$v.$touch()
      if (this.$v.form.$invalid || !this.rentId) return

      this.creating = true
      try {
        await services.rents.addExtraTime({
          id: this.rentId,
          time: this.form.time,
          reason: this.form.reason
        })
        this.$root?.$toast?.success?.('Tempo extra adicionado com sucesso')

        await this.loadExistingExtras()

        this.$emit('changed', {
          type: 'extra-time-added',
          minutes: this.form.time,
          reason: this.form.reason
        })

        this.closeModal()

        this.form.time = 0
        this.form.reason = null
        this.otherSelected = false
        this.$v.$reset()
      } catch (e) {
        this.$root?.$toast?.error?.('Não foi possível adicionar o tempo extra')
      } finally {
        this.creating = false
      }
    },

    async loadExistingExtras() {
      if (!this.rentId) {
        this.extras = []
        return
      }

      this.loadingExtras = true
      try {
        const res = await services.rents.fetchRentById(this.rentId)
        const rent = res?.data || res

        const list =
          rent?.extraTime ||
          rent?.rent?.extraTime ||
          rent?.extra_times ||
          rent?.rent?.extra_times ||
          []

        this.extras = this.normalizeExtras(list)
      } catch (e) {
        this.extras = []
        this.$root?.$toast?.error?.('Erro ao carregar tempos extras')
      } finally {
        this.loadingExtras = false
      }
    },

    normalizeExtras(list) {
      return (list || [])
        .map((x) => ({
          id: String(x.id ?? x.extraTimeId ?? x.extra_id ?? x.ID),
          minutes: Number(x.minutes ?? x.time ?? 0),
          reason: x.reason ?? x.motivo ?? '',
          type: x.type ?? null,
          createdAt: x.createdAt ?? x.created_at ?? null,
          updatedAt: x.updatedAt ?? x.updated_at ?? null
        }))
        .filter((x) => x.id != null && x.id !== 'undefined')
        .sort((a, b) => {
          const da = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const db = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return db - da
        })
    },

    beginEdit(item) {
      this.editingId = String(item.id)
      this.editForm.minutes = item.minutes
      this.editForm.reason = item.reason || ''
    },
    cancelEdit() {
      this.editingId = null
      this.editForm.minutes = null
      this.editForm.reason = ''
    },

    async saveEdit(item) {
      if (!this.canSaveEdit) return

      this.saving = true
      try {
        await services.rents.updateExtraTime({
          rentId: this.rentId,
          extraId: item.id,
          minutes: this.editForm.minutes,
          reason:
            this.editForm.reason && this.editForm.reason.trim() !== ''
              ? this.editForm.reason
              : undefined
        })

        const idx = this.extras.findIndex(
          (e) => String(e.id) === String(item.id)
        )
        if (idx !== -1) {
          this.$set(this.extras, idx, {
            ...this.extras[idx],
            minutes: this.editForm.minutes,
            reason: this.editForm.reason || '',
            updatedAt: new Date().toISOString()
          })
        }

        this.$root?.$toast?.success?.('Tempo extra atualizado com sucesso')
        this.cancelEdit()
        this.$emit('changed', { type: 'extra-time-updated', extraId: item.id })
      } catch (e) {
        this.$root?.$toast?.error?.('Não foi possível atualizar o tempo extra')
      } finally {
        this.saving = false
      }
    },

    async onClickRemove(item) {
      const idStr = String(item.id)

      // Limita o motivo a 10 caracteres + reticências (quando houver)
      const truncatedReason =
        item.reason?.length > 10
          ? item.reason.slice(0, 10) + '...'
          : item.reason || 'sem motivo'

      const message = `Remover o tempo extra de ${item.minutes} min ("${truncatedReason}")?`

      const ok = await this.$bvModal?.msgBoxConfirm?.(message, {
        title: 'Confirmar remoção',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Remover',
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        centered: true
      })

      if (!ok) return

      if (!this.rentId) {
        this.$root?.$toast?.error?.('Aluguel inválido')
        return
      }

      this.removingId = idStr
      try {
        await services.rents.removeExtraTime({
          rentId: this.rentId,
          extraId: idStr
        })

        // remove localmente
        const idx = this.extras.findIndex((e) => String(e.id) === idStr)
        if (idx !== -1) this.extras.splice(idx, 1)

        this.$root?.$toast?.success?.(
          `Tempo extra (${item.minutes} min) removido`
        )
        this.$emit('changed', { type: 'extra-time-removed', extraId: idStr })
      } catch (e) {
        this.$root?.$toast?.error?.('Não foi possível remover o tempo extra')
      } finally {
        this.removingId = null
      }
    },

    formatDateTime(dt) {
      if (!dt) return '—'
      try {
        const d = typeof dt === 'string' ? new Date(dt) : dt
        return new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short'
        }).format(d)
      } catch {
        return dt
      }
    },

    closeModal() {
      this.$emit('should-close')
      if (this.modalId) {
        this.$root?.$emit?.('bv::hide::modal', this.modalId)
      }
    }
  }
}
</script>

<style scoped>
/* BADGE + MOTIVO */
.reason-ellipsis {
  max-width: 240px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.id-chip {
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 4px;
}

/* WRAPPERS */
.extra-times-wrapper {
  max-width: 450px;
  margin: 0 auto;
}
.extras-list-container {
  max-height: 280px;
  overflow-y: auto;
}

/* COLUNAS */
.content-col {
  flex-grow: 1;
  padding-right: 1rem;
  min-width: 0;
}

/* AÇÕES (VERTICAL) */
.actions-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  min-width: 44px;
}
.action-btn {
  padding: 0.125rem;
  line-height: 1;
}
.action-inline {
  padding: 0.125rem 0.25rem;
}

/* EDIÇÃO INLINE */
.edit-grid {
  display: grid;
  grid-template-columns: 90px 1fr;
  grid-gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.reason-text {
  word-break: break-word;
  white-space: normal;
}

/* RÓTULO AO LADO DOS ÍCONES */
.action-label {
  font-size: 12px;
  opacity: 0.85;
}

/* LIST ITEM + TIPOS (faixa lateral) */
.extra-item {
  border-left: 4px solid transparent;
  transition: background 0.15s ease;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.extra-item:hover {
  background: #fafafa;
}
.type-pause {
  border-left-color: #17a2b8;
} /* info */
.type-bonus {
  border-left-color: #28a745;
} /* success */
.type-default {
  border-left-color: #6c757d;
} /* secondary */

/* SKELETON LOADING */
.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 4px;
}
.skeleton-line.w-75 {
  width: 75%;
}
.skeleton-line.w-50 {
  width: 50%;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
